import * as React from 'react';

import {Button} from 'shells/button/button';
import {Line} from 'shells/divider/line';
import {CardWidgetEmptyState} from 'shells/empty-states/card-widget-empty-state';
import {LeadIcon} from 'shells/icon/svg/lead-icon';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {LeadTwoLine} from 'shells/lead/lead-two-line';
import {EntityPageAddItemButton} from 'shells/lead-sidebar/entity-page-add-item-button';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {FormattedTimestamp} from 'shells/timestamp/formatted-timestamp';
import {Body} from 'shells/typography/body';

import {appHistory} from '../../../history';

import {GraphQLSelectLeads} from '../../core/select/graphql-select-leads';
import {useGetRecentLeads} from '../../core/select/hooks';
import {secondaryInfo} from '../../navigation/navbar/search-bar/search-results/search-result-lead';

import styles from './select-related-lead-form.module.css';

type Props = {
    onSelect: (leadId: string) => void;
};

export function SelectRelatedLeadForm(props: Props) {
    const {recentLeads, isLoading} = useGetRecentLeads();

    const handleAddNewLead = React.useCallback(() => {
        appHistory.replace({
            ...appHistory.location,
            hash: '/create/leads',
            state: {modal: true},
        });
    }, []);

    return (
        <>
            <StackLegacy spacing={24}>
                <StackLegacy spacing={8}>
                    <Body color='dark'>Search a lead</Body>
                    <GraphQLSelectLeads
                        selectedValues={[]}
                        onChange={(lead) => {
                            // @ts-expect-error
                            if (!Array.isArray(lead) && lead.id) {
                                // @ts-expect-error
                                props.onSelect(lead.id);
                            }
                        }}
                        isMulti={false}
                        searchable={true}
                        controlShouldRenderValue={false}
                        autoFocus={true}
                        placeholder='Select a lead'
                    />
                </StackLegacy>
                <Line text='or' darkText={true} />
                {isLoading ? (
                    <>
                        <div className='mb-24'>
                            <Body color='dark'>Select a recent lead</Body>
                        </div>
                        <LoadingPage />
                    </>
                ) : (
                    <>
                        <div className='mb-16'>
                            <Body color='dark'>Select a recent lead</Body>
                        </div>
                        {recentLeads.length === 0 && (
                            <div className={styles['empty-state-container']}>
                                <CardWidgetEmptyState
                                    text='No recent leads'
                                    icon={LeadIcon}
                                    iconSize={24}
                                />
                            </div>
                        )}
                        {recentLeads.length > 0 &&
                            recentLeads.map((lead) => {
                                const primaryAccount =
                                    lead.accounts && lead.accounts.primaryEdge
                                        ? lead.accounts.primaryEdge.node
                                        : undefined;
                                const primaryContact =
                                    lead.contacts && lead.contacts.primaryEdge
                                        ? lead.contacts.primaryEdge.node
                                        : undefined;

                                return (
                                    <Button
                                        key={lead.id}
                                        onClick={() => props.onSelect(lead.id)}
                                        variant='upgrade'
                                        isFullWidth={true}
                                        noBorder={true}
                                        noPadding={true}
                                    >
                                        <LeadTwoLine
                                            noCenterAlignTopRow={true}
                                            largerLeadName={true}
                                            relatedCompany={primaryAccount}
                                            relatedPerson={primaryContact}
                                            leadName={lead.name}
                                            leadNumber={lead.number}
                                            leadStatus={lead.status}
                                            htmlUrl={lead.htmlUrl}
                                            priority={lead.priority}
                                            isLinkable={false}
                                            avatarUrl={lead.avatarUrl}
                                            noAssociatedEntityLineHeight={true}
                                            rightContent={
                                                <div className={styles['formatted-timestamp']}>
                                                    <FormattedTimestamp
                                                        datetime={lead.createdTime}
                                                        format={
                                                            FormattedTimestamp.formats.shortMonthDay
                                                        }
                                                    />
                                                </div>
                                            }
                                        />
                                        <div className={styles['secondary-info']}>
                                            {secondaryInfo(
                                                lead.assignee,
                                                lead.value && lead.value.formattedValue
                                            )}
                                        </div>
                                    </Button>
                                );
                            })}
                    </>
                )}
            </StackLegacy>
            <div className='my-16'>
                <EntityPageAddItemButton
                    sectionEntity='contacts'
                    parentItemText='a new lead'
                    omitEntityName={true}
                    onAddItem={handleAddNewLead}
                />
            </div>
        </>
    );
}
